import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, ClipboardList, Timer } from 'lucide-react';
import { TeamLogo } from '@/lib/teamLogos';
import { useAuth } from '@/contexts/AuthContext';
import MatchDetailDialog, { MATCH_LABELS } from '@/components/MatchDetailDialog';

const STATUS_COLOR = {
  submitted: 'bg-blue-50 text-blue-600', under_review: 'bg-amber-50 text-amber-600',
  approved: 'bg-green-50 text-green-600', partially_approved: 'bg-yellow-50 text-yellow-600',
  rejected: 'bg-red-50 text-red-600', cancelled: 'bg-gray-100 text-gray-500',
  cancellation_requested: 'bg-amber-50 text-amber-600', pending_sponsor_approval: 'bg-purple-50 text-purple-600',
  delivered: 'bg-teal-50 text-teal-700',
};

const STATUS_BORDER = {
  submitted: 'border-l-blue-500', under_review: 'border-l-amber-500',
  approved: 'border-l-green-500', partially_approved: 'border-l-yellow-500',
  rejected: 'border-l-red-500', cancelled: 'border-l-gray-400',
  cancellation_requested: 'border-l-amber-500', pending_sponsor_approval: 'border-l-purple-500',
  delivered: 'border-l-teal-500',
};

const ACTIONABLE_STATUSES = ['submitted', 'under_review', 'pending_sponsor_approval'];

export function StatCards({ cards, requestCard }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
      {cards.map(c => (
        <Link key={c.label} to={c.link}>
 <Card className="bg-white dark:bg-[var(--surface)] shadow-card hover:shadow-card transition-all duration-200 cursor-pointer group rounded-card">
            <CardContent className="px-5 py-4 flex items-center gap-4">
              <div className={`h-10 w-10 rounded-[12px] flex items-center justify-center ${c.bg} shrink-0 group-hover:scale-105 transition-transform duration-200`}>
                <c.icon size={20} className={c.color} strokeWidth={1.7} />
              </div>
              <div>
                <p className="tabular-nums text-[22px] font-medium text-gray-900 dark:text-gray-50 leading-none">{c.value}</p>
                <p className="section-title mt-1 whitespace-nowrap">{c.label}</p>
              </div>
            </CardContent>
          </Card>
        </Link>
      ))}
      {requestCard && (
        <Link to={requestCard.link}>
 <Card className="bg-white dark:bg-[var(--surface)] shadow-card hover:shadow-card transition-all duration-200 cursor-pointer group rounded-card">
            <CardContent className="px-5 py-4 flex items-center gap-3 min-w-0">
              <div className={`h-10 w-10 rounded-[12px] flex items-center justify-center ${requestCard.bg} shrink-0 group-hover:scale-105 transition-transform duration-200`}>
                <requestCard.icon size={20} className={requestCard.color} strokeWidth={1.7} />
              </div>
              <div className="min-w-0">
                <p className="tabular-nums text-[22px] font-medium text-gray-900 dark:text-gray-50 leading-none">{requestCard.value}</p>
                <p className="section-title mt-1 truncate">{requestCard.label}</p>
              </div>
              <div className="ms-auto space-y-0.5 shrink-0 min-w-0">
                {requestCard.breakdown.map(b => (
                  <div key={b.label} className="flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${b.dotColor}`} />
                    <span className="hidden 2xl:inline text-[11px] text-gray-400 whitespace-nowrap">{b.label}</span>
                    <span className="tabular-nums text-xs font-medium text-gray-700 dark:text-gray-300">{b.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </Link>
      )}
    </div>
  );
}

export function UpcomingMatches({ thisRoundMatches, thisRound, nextRoundMatches, nextRound, size = 'small' }) {
  const { t } = useTranslation();
  const [selectedMatch, setSelectedMatch] = useState(null);
  const hasThis = thisRoundMatches?.length > 0;
  const hasNext = nextRoundMatches?.length > 0;
  const [filter, setFilter] = useState(hasThis ? 'this' : 'next');
  const [countdown, setCountdown] = useState('');

  const parseMatchDateTime = (m) => {
    if (!m?.match_date) return null;
    let d = new Date(m.match_date);
    if (isNaN(d.getTime())) {
      const parts = m.match_date.split('/');
      if (parts.length === 3) d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
      else return null;
    }
    if (m.kickoff_time) {
      const [h, min] = m.kickoff_time.split(':').map(Number);
      if (!isNaN(h)) d.setHours(h, min || 0, 0, 0);
    }
    return d;
  };

  // Countdown to soonest upcoming match across both rounds
  useEffect(() => {
    const calcCountdown = () => {
      const allM = [...(thisRoundMatches || []), ...(nextRoundMatches || [])];
      let soonest = null;
      for (const m of allM) {
        const dt = parseMatchDateTime(m);
        if (dt && dt > new Date()) {
          if (!soonest || dt < soonest) soonest = dt;
        }
      }
      if (!soonest) { setCountdown(''); return; }
      const diff = soonest.getTime() - Date.now();
      if (diff <= 0) { setCountdown(''); return; }
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      if (days > 0) setCountdown(`${days}d ${hours}h ${mins}m`);
      else if (hours > 0) setCountdown(`${hours}h ${mins}m`);
      else setCountdown(`${mins}m`);
    };
    calcCountdown();
    const interval = setInterval(calcCountdown, 60000);
    return () => clearInterval(interval);
  }, [thisRoundMatches, nextRoundMatches]);

  // Update default filter when data changes
  useEffect(() => {
    if (hasThis) setFilter('this');
    else if (hasNext) setFilter('next');
  }, [hasThis, hasNext]);

  const filteredMatches = filter === 'this' ? (thisRoundMatches || []) : (nextRoundMatches || []);
  const activeRound = filter === 'this' ? thisRound : nextRound;
  if (!hasThis && !hasNext) return null;
  const isWide = size === 'large' || size === 'full';

  const filterPills = [];
  if (hasThis) filterPills.push({ key: 'this', label: t('this_round', 'This Round') });
  if (hasNext) filterPills.push({ key: 'next', label: t('next_round', 'Next Round') });

  return (
    <>
 <Card data-testid="upcoming-round-card" className="bg-white dark:bg-[var(--surface)] shadow-card rounded-card flex flex-col lg:h-full">
        <CardHeader className="pb-2 pt-4 px-5 shrink-0">
          <CardTitle className="text-xs lg:section-title flex items-center gap-2.5">
            <div className="p-1.5 rounded-lg bg-amber-50 dark:bg-amber-950">
              <Calendar size={14} className="text-amber-500" />
            </div>
            {t('upcoming_matches')}
            {filterPills.length > 0 && (
              <div className="flex items-center gap-1 ml-auto mr-2">
                {filterPills.map(f => (
                  <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`match-filter-${f.key}`}
                    className={`px-2 py-0.5 rounded-full text-[9px] lg:text-[10px] font-medium transition-colors ${
                      filter === f.key
                        ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400'
                        : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}>
                    {f.label}
                  </button>
                ))}
              </div>
            )}
            <Badge variant="outline" className="text-[9px] lg:text-xs font-medium text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700 shrink-0">{filteredMatches.length}</Badge>
          </CardTitle>
          {countdown && (
            <div data-testid="match-countdown" className="flex items-center gap-1.5 px-3 py-1.5 rounded-[14px] bg-amber-50 dark:bg-amber-950/50 mt-1.5">
              <Timer size={13} className="text-amber-500 shrink-0" />
              <span className="text-[11px] lg:text-xs font-semibold text-amber-700 dark:text-amber-400">
                {t('next_kickoff', 'Next kickoff in')} <span className="tabular-nums">{countdown}</span>
              </span>
            </div>
          )}
        </CardHeader>
        <CardContent className="px-5 pb-4 pt-0 flex-1 min-h-0 flex flex-col">
          <div className="card-scroll">
            {filteredMatches.length === 0 ? (
              <p className="text-gray-400 text-sm h-full flex items-center justify-center">{t('no_data') || 'No matches found'}</p>
            ) : filteredMatches.map(m => (
              <div key={m.id} onClick={() => setSelectedMatch(m)} className="card-row block px-1 py-2 cursor-pointer">
                {/* Desktop row */}
                <div className="hidden sm:flex items-center gap-2.5">
                  <Badge variant="outline" className="text-[9px] lg:text-xs bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 px-1.5 py-0 shrink-0 tabular-nums font-semibold">{m.round || activeRound}</Badge>
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <TeamLogo name={m.home_team} badgeUrl={m.home_team_badge} size={18} />
                    <span className="text-[11px] font-semibold text-gray-800 dark:text-gray-200">{m.home_team}</span>
                  <span className="text-[9px] lg:text-[10px] text-gray-300 dark:text-gray-600 shrink-0 font-medium">vs</span>
                    <TeamLogo name={m.away_team} badgeUrl={m.away_team_badge} size={18} />
                    <span className="text-[11px] font-semibold text-gray-800 dark:text-gray-200">{m.away_team}</span>
                  </div>
                  {isWide && m.label && MATCH_LABELS[m.label] && (
                    <Badge className={`text-[8px] lg:text-[11px] pointer-events-none select-none shrink-0 ${MATCH_LABELS[m.label].color}`}>{MATCH_LABELS[m.label].name}</Badge>
                  )}
                  {isWide && m.stadium && (
                    <span className="text-[9px] lg:text-xs text-gray-400 flex items-center gap-0.5 flex-1 justify-center truncate">
                      <MapPin size={9} className="shrink-0" /> {m.stadium}{m.city ? `, ${m.city}` : ''}
                    </span>
                  )}
                  <span className="text-[9px] lg:text-xs text-gray-400 shrink-0 ml-auto tabular-nums flex items-center gap-2 font-medium">
                    <span>{m.match_date}</span>
                    {m.kickoff_time && <span className="text-gray-500 dark:text-gray-400">{m.kickoff_time}</span>}
                  </span>
                </div>
                {/* Mobile: stacked rows, no truncation */}
                <div className="sm:hidden space-y-1.5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className="text-[9px] lg:text-xs bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 px-1.5 py-0 shrink-0 tabular-nums font-semibold">{m.round || activeRound}</Badge>
                    <TeamLogo name={m.home_team} badgeUrl={m.home_team_badge} size={18} />
                    <span className="text-[11px] lg:text-[15px] font-semibold text-gray-800 dark:text-gray-200">{m.home_team}</span>
                    <span className="text-[9px] text-gray-300 dark:text-gray-600 font-medium">vs</span>
                    <TeamLogo name={m.away_team} badgeUrl={m.away_team_badge} size={18} />
                    <span className="text-[11px] lg:text-[15px] font-semibold text-gray-800 dark:text-gray-200">{m.away_team}</span>
                    {m.label && MATCH_LABELS[m.label] && (
                      <Badge className={`text-[8px] lg:text-[11px] pointer-events-none select-none shrink-0 ${MATCH_LABELS[m.label].color}`}>{MATCH_LABELS[m.label].name}</Badge>
                    )}
                  </div>
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 text-[9px] lg:text-xs text-gray-400 pl-6">
                    <span className="tabular-nums font-medium">{m.match_date} {m.kickoff_time || ''}</span>
                    {m.stadium && (
                      <span className="flex items-center gap-0.5">
                        <MapPin size={9} className="shrink-0 text-gray-300" /> {m.stadium}{m.city ? `, ${m.city}` : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <MatchDetailDialog
        match={selectedMatch}
        onClose={() => setSelectedMatch(null)}
        onMatchUpdate={(updated) => setSelectedMatch(updated)}
      />
    </>
  );
}

export function RecentRequests({ requests, showSponsor = true, size = 'full' }) {
  const { t } = useTranslation();
  const displayRequests = requests?.slice(0, 4);

  return (
 <Card className="bg-white dark:bg-[var(--surface)] shadow-card rounded-card flex flex-col lg:h-full">
      <CardHeader className="pb-2 pt-4 px-5 shrink-0">
        <CardTitle className="section-title flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-blue-50 dark:bg-blue-950">
            <ClipboardList size={15} className="text-blue-500" />
          </div>
          {t('recent_requests')}
          {displayRequests?.length > 0 && (
            <Badge variant="outline" className="ml-auto text-[9px] lg:text-xs font-medium text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700">{displayRequests.length} requests</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-4 pt-0 flex-1 min-h-0 overflow-y-auto">
        {(!displayRequests?.length) ? (
          <p className="text-gray-400 text-[13px] h-full flex items-center justify-center">{t('no_data')}</p>
        ) : (
          <div>
            {displayRequests.map(r => {
              return (
                <Link key={r.id} to={`/requests/${r.id}`} className="card-row block py-2 px-1 group" data-testid={`recent-request-${r.id}`}>
                  {/* Line 1: request number + sponsor */}
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[11px] font-mono text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded shrink-0">#{r.request_number}</span>
                    <span className="text-xs lg:section-title truncate group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                      {showSponsor
                        ? r.sponsor_name
                        : (r.home_team ? r.match_title : t('no_match_linked', 'No match linked'))}
                    </span>
                  </div>
                  {/* Line 2: club logos + match + date, status on the right */}
                  <div className="flex items-center gap-2 mt-1.5 min-w-0">
                    {r.home_team ? (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <TeamLogo name={r.home_team} badgeUrl={r.home_team_badge} size={16} />
                        <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300 truncate">{r.home_team}</span>
                        <span className="text-[9px] lg:text-[10px] text-gray-300 dark:text-gray-600 shrink-0 font-medium">vs</span>
                        <TeamLogo name={r.away_team} badgeUrl={r.away_team_badge} size={16} />
                        <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300 truncate">{r.away_team}</span>
                      </div>
                    ) : showSponsor ? (
                      <span className="text-[11px] text-gray-400 italic truncate">{t('no_match_linked', 'No match linked')}</span>
                    ) : null}
                    {r.match_date && (
                      <span className="text-[11px] text-gray-400 shrink-0 whitespace-nowrap tabular-nums">- {r.match_date}</span>
                    )}
                    <Badge className={`text-[9px] lg:text-xs px-2 py-0.5 font-medium shrink-0 whitespace-nowrap capitalize pointer-events-none select-none ml-auto ${STATUS_COLOR[r.request_status] || 'bg-gray-100 text-gray-600'}`}>
                      {r.request_status === 'pending_sponsor_approval' ? 'Pending' : r.request_status?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function UpcomingRoundRequests({ data, showSponsorTabs = true }) {
  const { t } = useTranslation();
  const [roundIdx, setRoundIdx] = useState(0);
  const [tab, setTab] = useState('all');
  const blocks = data?.rounds || [];
  const block = blocks[roundIdx] || blocks[0];
  const requests = block?.requests || [];
  const sponsors = block?.sponsors || [];
  const shownRequests = tab === 'all' ? requests : requests.filter(r => r.sponsor_id === tab);
  const activeSponsor = sponsors.find(s => s.id === tab);
  const totals = activeSponsor ? activeSponsor.rights : (block?.rights || []);

  const switchRound = (i) => { setRoundIdx(i); setTab('all'); };

  return (
 <Card data-testid="upcoming-round-requests-card" className="bg-white dark:bg-[var(--surface)] shadow-card rounded-card flex flex-col lg:h-full">
      <CardHeader className="pb-2 pt-4 px-5 shrink-0">
        <CardTitle className="section-title flex items-center gap-2.5">
          <div className="p-1.5 rounded-lg bg-amber-50 dark:bg-amber-950">
            <Timer size={15} className="text-amber-500" />
          </div>
          {block?.round ? `Round ${block.round} Requests` : 'Upcoming Round Requests'}
          {blocks.length > 1 && (
            <div className="ms-auto flex items-center gap-1">
              {blocks.map((b, i) => (
                <button
                  key={b.round || i}
                  data-testid={`upcoming-round-switch-${i}`}
                  onClick={() => switchRound(i)}
                  className={`text-[10px] lg:text-[11px] px-2.5 py-1 rounded-full transition-colors ${roundIdx === i
                    ? 'tint-brand font-semibold'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'}`}
                >
                  {i === 0 ? 'This Round' : 'Next Round'}
                </button>
              ))}
            </div>
          )}
        </CardTitle>
        {block?.round && (
          <p className="text-[11px] text-gray-400 ps-10">
            Round {block.round} · {block.match_count} {block.match_count === 1 ? 'match' : 'matches'}
            {block.date_from && ` · ${block.date_from}${block.date_to && block.date_to !== block.date_from ? ` → ${block.date_to}` : ''}`}
            {` · ${block.total_requests} requests · ${block.total_units} units`}
          </p>
        )}
      </CardHeader>

      {showSponsorTabs && sponsors.length > 0 && (
        <div className="px-5 pb-2 shrink-0">
          <div className="flex items-center gap-1 overflow-x-auto pb-1">
            <button
              data-testid="upcoming-round-tab-all"
              onClick={() => setTab('all')}
              className={`shrink-0 text-[11px] px-2.5 py-1 rounded-full border transition-colors ${tab === 'all'
                ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white'
                : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            >
              All <span className="tabular-nums opacity-70">{requests.length}</span>
            </button>
            {sponsors.map(s => (
              <button
                key={s.id}
                data-testid={`upcoming-round-tab-${s.id}`}
                onClick={() => setTab(s.id)}
                className={`shrink-0 text-[11px] px-2.5 py-1 rounded-full border transition-colors truncate max-w-[180px] ${tab === s.id
                  ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white'
                  : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                {s.name} <span className="tabular-nums opacity-70">{s.request_count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <CardContent className="px-5 pb-4 pt-0 flex-1 min-h-0 overflow-y-auto">
        {totals.length > 0 && (
          <div data-testid="upcoming-round-totals" className="flex flex-wrap gap-1.5 mb-3">
            {totals.map(r => (
              <span key={r.right_name} className="text-[10px] lg:text-[11px] px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                {r.right_name} <b className="text-gray-900 dark:text-white tabular-nums">{r.quantity}</b>
              </span>
            ))}
          </div>
        )}

        {tab === 'all' && showSponsorTabs && sponsors.length > 0 ? (
          <div>
            {sponsors.map(s => (
              <button
                key={s.id}
                data-testid={`upcoming-round-sponsor-row-${s.id}`}
                onClick={() => setTab(s.id)}
                className="card-row w-full text-start py-2 px-1"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xs lg:section-title truncate">{s.name}</span>
                  <span className="text-[11px] text-gray-400 shrink-0 ms-auto tabular-nums">{s.request_count} {s.request_count === 1 ? 'request' : 'requests'}</span>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                  {s.rights.map(r => (
                    <span key={r.right_name} className="text-[11px] text-gray-500 dark:text-gray-400">
                      {r.right_name} <b className="text-gray-700 dark:text-gray-200 tabular-nums">{r.quantity}</b>
                    </span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        ) : shownRequests.length === 0 ? (
          <p data-testid="upcoming-round-empty" className="text-gray-400 text-[13px] h-full flex items-center justify-center text-center">
            {block?.round ? `No requests booked for Round ${block.round} yet` : t('no_data')}
          </p>
        ) : (
          <div>
            {shownRequests.map(r => (
              <Link
                key={r.id}
                to={`/requests/${r.id}`}
                data-testid={`upcoming-round-request-${r.id}`}
                className="card-row block py-2 px-1"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[11px] font-mono text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded shrink-0">#{r.request_number}</span>
                  <span className="text-xs lg:section-title truncate">{r.sponsor_name}</span>
                  <Badge className={`text-[9px] lg:text-xs px-2 py-0.5 font-medium shrink-0 capitalize pointer-events-none select-none ms-auto ${STATUS_COLOR[r.request_status] || 'bg-gray-100 text-gray-600'}`}>
                    {r.request_status === 'pending_sponsor_approval' ? 'Pending' : r.request_status?.replace(/_/g, ' ')}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 mt-1.5 min-w-0">
                  <span className="text-[11px] font-medium text-gray-600 dark:text-gray-300 truncate">{r.match_label || t('no_match_linked', 'No match linked')}</span>
                  {r.kickoff_time && <span className="text-[10px] text-gray-400 shrink-0 tabular-nums">{r.kickoff_time}</span>}
                  <span className="text-[11px] text-gray-400 truncate ms-auto">
                    {r.items.map(i => `${i.right_name} ×${i.quantity}`).join(', ')}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
